import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import { useCarouselAutoplay } from "../hooks/useCarouselAutoplay";
import { LOGIN_PROMO_SLIDES } from "./loginPromoSlides";

export function LoginPromoCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const { resetAutoplay } = useCarouselAutoplay(emblaApi);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());

    onSelect();
    emblaApi.on("reInit", onSelect).on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onSelect).off("select", onSelect);
    };
  }, [emblaApi]);

  function goToSlide(index: number) {
    emblaApi?.scrollTo(index);
    resetAutoplay();
  }

  function goToPrev() {
    emblaApi?.scrollPrev();
    resetAutoplay();
  }

  function goToNext() {
    emblaApi?.scrollNext();
    resetAutoplay();
  }

  return (
    <div className="group relative flex h-full w-full flex-col overflow-hidden rounded-4xl bg-login-panel-bg shadow-xl">
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {LOGIN_PROMO_SLIDES.map((slide) => (
            <div
              className="flex h-full min-w-0 flex-[0_0_100%] flex-col"
              key={slide.href}
            >
              <img
                alt=""
                className="h-auto w-full shrink-0"
                src={slide.image}
              />
              <div className="flex flex-1 flex-col justify-center gap-2xl px-5xl py-2xl">
                <span className="w-fit rounded-full bg-login-accent px-[10px] py-xxs text-sm font-medium text-login-heading">
                  {slide.badge}
                </span>
                <p className="text-display-xs font-bold text-white xl:text-display-sm">
                  {slide.headline}
                </p>
                <p className="text-sm text-login-promo-body">{slide.body}</p>
                <a
                  className="flex items-center gap-xs text-sm text-login-promo-link"
                  href={slide.href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Learn more
                  <ArrowRight className="size-6" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center gap-lg py-[31px]">
        {LOGIN_PROMO_SLIDES.map((slide, index) => (
          <button
            aria-label={`Go to slide ${index + 1}`}
            className={`size-[10px] rounded-full transition-colors ${
              index === selectedIndex
                ? "bg-login-accent"
                : "bg-login-dot-inactive"
            }`}
            key={slide.href}
            onClick={() => goToSlide(index)}
            type="button"
          />
        ))}
      </div>

      <button
        aria-label="Previous slide"
        className="absolute left-xl top-1/2 -translate-y-1/2 text-white/80 opacity-0 transition-opacity group-hover:opacity-100 hover:text-white"
        onClick={goToPrev}
        type="button"
      >
        <ChevronLeft className="size-6" />
      </button>
      <button
        aria-label="Next slide"
        className="absolute right-xl top-1/2 -translate-y-1/2 text-white/80 opacity-0 transition-opacity group-hover:opacity-100 hover:text-white"
        onClick={goToNext}
        type="button"
      >
        <ChevronRight className="size-6" />
      </button>
    </div>
  );
}
